import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

// components
import { ExecutionModalComponent } from '../../modals/execution-modal/execution-modal.component';

// services
import { HttpService } from '../../../core/services/http.service';
import { PlanogramsService } from '../../../core/services/planograms.service';
import { PlansService } from '../../../core/services/plans.service';
import { SnackBarService } from '../../../core/services/snack-bar.service';
import { SocketGroup, SocketService } from '../../../core/services/socket.service';
import { StoresService } from '../../../core/services/stores.service';

interface PlanogramsData {
	done_execution: any[];
	send_execution: any[];
	scheduled_execution: any[];
}

@Component({
    selector: 'app-planograms-workspace',
    templateUrl: './planograms-workspace.component.html',
    styleUrls: ['./planograms-workspace.component.scss'],
    standalone: false
})
export class PlanogramsWorkspaceComponent implements OnInit, OnDestroy {
	loader: boolean = false;
	planograms: PlanogramsData = {
		done_execution: [],
		send_execution: [],
		scheduled_execution: [],
	};

	shop_plans: any[] = [];
	shops: any[] = [];

	private subscription: Subscription;
	private timer: any;

	private readonly titleMap: Record<string, string> = {
		done_execution: 'EXECUTED_PLANOGRAMS',
		send_execution: 'PLANOGRAMS_ON_EXECUTION',
		scheduled_execution: 'SCHEDULED_PLANOGRAMS.PLANNED_DATES',
	};

	hasFailedScheduledSend: number = 0;

	constructor(
		private dialog: MatDialog,
		private httpService: HttpService,
		private planogramsService: PlanogramsService,
		private plansService: PlansService,
		private snackBarService: SnackBarService,
		private socketService: SocketService,
		private storesService: StoresService
	) {}

	async ngOnInit(): Promise<void> {
		this.loader = true;

		this.socketService.addSubscription(SocketGroup.UpdateExecution);
		this.subscription = this.socketService.updateExecution.subscribe((): void => {
			if (this.timer) return;

			this.loader = true;
			this.timer = setTimeout((): Promise<void> => this.loadPlanograms(), 1000);
		});

		await this.loadInitialData();
		this.loader = false;
	}

	private async loadInitialData(): Promise<void> {
		const [planograms, shop_plans, shops] = await Promise.all([
			this.planogramsService.dashboardExecutions(),
			this.plansService.get(),
			this.httpService.getPromise(this.storesService.getShops$)
		]);

		this.planograms = planograms;
		this.shop_plans = shop_plans;
		this.shops = shops;
		this.checkFailedScheduledSend();
	}

	private async loadPlanograms(): Promise<void> {
		this.planograms = await this.planogramsService.dashboardExecutions();
		this.loader = false;
		this.checkFailedScheduledSend();

		const [dialogRef] = this.dialog.openDialogs;
		if (dialogRef?.componentInstance instanceof ExecutionModalComponent) {
			const modal: ExecutionModalComponent = dialogRef.componentInstance;
			modal.data.data = this.planograms[modal.data.type];
			modal.ngOnInit();
		}

		this.timer = null;
	}

	private checkFailedScheduledSend(): void {
		this.planograms.scheduled_execution.forEach(execution => {
			if (execution.failed_scheduled_send === true) {
				this.hasFailedScheduledSend++;
			}
		});

	}

	openDetailModal(type: keyof PlanogramsData): void {
		if (!this.titleMap[type]) {
			console.warn('Unknown execution type:', type);
			return;
		}

		const dialogRef: MatDialogRef<ExecutionModalComponent> = this.dialog.open(ExecutionModalComponent, {
			width: '75%',
			height: 'auto',
			autoFocus: false,
			backdropClass: 'back-drop',
			data: {
				data: this.planograms[type].map(p => ({ ...p })),
				title: this.titleMap[type],
				plans: [{ id: -1, name: 'ALL', active: true }, ...this.shop_plans],
				shops: this.shops,
				type
			}
		});

		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				this.snackBarService.setSnackBarNotification({
					messageText: 'THREAD_EXECUTION_MESSAGE'
				});
			}
		});
	}

	ngOnDestroy(): void {
		if (this.subscription) this.subscription.unsubscribe();
		if (this.timer) clearTimeout(this.timer);
		this.socketService.deleteSubscription(SocketGroup.UpdateExecution);
	}
}
